import { useCallback, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import "./HeaderNotifications.css";

const HeaderNotifications = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [markingAllAsRead, setMarkingAllAsRead] = useState(false);

    const loadNotifications = useCallback(async () => {
        try {
            const response = await axiosInstance.get(Api.notification.base, {
                params: { limit: 5 }
            });

            setNotifications(response.data?.notifications || []);
            setUnreadCount(response.data?.unreadCount || 0);
        } catch (error) {
            console.error("Error loading notifications:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotifications();

        const intervalId = window.setInterval(() => {
            loadNotifications();
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, [loadNotifications]);

    const markAsReadLocally = (notificationId) => {
        setNotifications((current) =>
            current.map((notification) =>
                notification.id === notificationId
                    ? { ...notification, read: true }
                    : notification
            )
        );
        setUnreadCount((current) => Math.max(0, current - 1));
    };

    const markAllAsReadLocally = () => {
        setNotifications((current) =>
            current.map((notification) => ({ ...notification, read: true }))
        );
        setUnreadCount(0);
    };

    const resolveActionUrl = (notification) => {
        return notification?.actionUrl || null;
    };

    const getItemIconClass = (notification) => {
        switch (notification?.icon) {
            case "calendar":
                return "fa fa-calendar";
            case "message":
                return "fa fa-comment";
            case "warning":
                return "fa fa-exclamation";
            default:
                return "fa fa-circle";
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification) {
            return;
        }

        if (!notification.read) {
            try {
                await axiosInstance.patch(`${Api.notification.base}/${notification.id}/read`);
                markAsReadLocally(notification.id);
            } catch (error) {
                console.error("Error marking notification as read:", error);
            }
        }

        const actionUrl = resolveActionUrl(notification);
        if (actionUrl) {
            navigate(actionUrl);
        }
    };

    const handleMarkAllAsRead = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (markingAllAsRead || unreadCount === 0) {
            return;
        }

        try {
            setMarkingAllAsRead(true);
            await axiosInstance.patch(Api.notification.readAll);
            markAllAsReadLocally();
        } catch (error) {
            console.error("Error marking all notifications as read:", error);
        } finally {
            setMarkingAllAsRead(false);
        }
    };

    const formatCreatedAt = (createdAt) => {
        const parsed = moment(createdAt, "YYYY-MM-DD HH:mm:ss", true);
        return parsed.isValid() ? parsed.format("DD/MM [às] HH:mm") : "";
    };

    return (
        <Dropdown
            className="notification_dropdown"
            align="end"
            onToggle={(isOpen) => {
                if (isOpen) {
                    loadNotifications();
                }
            }}
        >
            <Dropdown.Toggle
                as="button"
                type="button"
                className="notification-trigger nav-link i-false"
                aria-label="Abrir central de eventos"
            >
                <span className="notification-trigger__icon" aria-hidden="true">
                    <i className="fas fa-bell"></i>
                </span>
                {unreadCount > 0 && (
                    <span className="badge notification-trigger__badge light text-white bg-danger rounded-circle">
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </Dropdown.Toggle>

            <Dropdown.Menu className="dropdown-menu-end">
                <div className="notification_title">
                    <div>
                        <h5 className="mb-0">Central de eventos</h5>
                        <small>{unreadCount} não lido{unreadCount === 1 ? "" : "s"}</small>
                    </div>
                    <button
                        type="button"
                        className="notification_title__action"
                        onClick={handleMarkAllAsRead}
                        disabled={markingAllAsRead || unreadCount === 0}
                    >
                        {markingAllAsRead ? "Lendo..." : "Ler todas"}
                    </button>
                </div>

                {loading ? (
                    <div className="notification-dropdown__empty">Carregando eventos...</div>
                ) : notifications.length === 0 ? (
                    <div className="notification-dropdown__empty">Nenhum evento recente.</div>
                ) : (
                    notifications.map((notification) => (
                        <Dropdown.Item
                            as="button"
                            type="button"
                            key={notification.id}
                            className={`notification-dropdown__item ${notification.read ? "" : "is-unread"} ${resolveActionUrl(notification) ? "is-clickable" : ""}`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <div className="d-flex align-items-start">
                                <div className="notification-dropdown__icon">
                                    <i className={getItemIconClass(notification)}></i>
                                </div>
                                <div className="notification-dropdown__content">
                                    <h6>{notification.title}</h6>
                                    <p>{notification.message}</p>
                                    <span className="notification-dropdown__time">
                                        {formatCreatedAt(notification.createdAt)}
                                    </span>
                                </div>
                            </div>
                        </Dropdown.Item>
                    ))
                )}

            </Dropdown.Menu>
        </Dropdown>
    );
};

export default HeaderNotifications;
